import React from 'react';
import { Box, ButtonBase, capitalize, CircularProgress, ClickAwayListener, InputBase, Popper, Slide, Typography } from '@mui/material';
import { Close, KeyboardArrowUp, Search } from '@mui/icons-material';
import { useTheme } from '@emotion/react';

// Floating bulk-action bar (ClickUp / Gmail pattern) shared by both Deal Flow views.
// Shows "N selected ×" and the bulk actions while something is selected, centred over
// the content area it is rendered in (measured, so the sidebar width never matters).
//
// actions: [{ key, label, icon, onClick, disabled, loading, highlight, menu }]
// menu (optional, opens a picker above the button instead of onClick):
//   { title, searchPlaceholder, options: [{ value, label }], optionIcon, onSelect(option), footer: [{ key, label, icon, onClick }] }
const BASE_BOTTOM = 24;
const AVOID_GAP = 12;

// avoidSelector (optional): an element the bar must sit above, e.g. a pagination footer.
const SelectionActionBar = ({ count = 0, onClear, actions = [], noun = 'selected', avoidSelector }) => {
    const theme = useTheme();
    const anchorRef = React.useRef(null);
    const [centerX, setCenterX] = React.useState(null);
    const [bottom, setBottom] = React.useState(BASE_BOTTOM);
    const [menuState, setMenuState] = React.useState(null); // { key, anchorEl }
    const open = count > 0;

    // Lift the bar above the avoided element's row while it is on screen, so it never covers it.
    React.useLayoutEffect(() => {
        if (!open || !avoidSelector) { setBottom(BASE_BOTTOM); return undefined; }
        const target = () => {
            const el = document.querySelector(avoidSelector);
            return el?.closest('.MuiGrid-container') || el;
        };
        const measure = () => {
            const rect = target()?.getBoundingClientRect();
            const visible = rect && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
            setBottom(visible ? Math.max(BASE_BOTTOM, window.innerHeight - rect.top + AVOID_GAP) : BASE_BOTTOM);
        };
        measure();
        const el = target();
        const observer = el ? new ResizeObserver(measure) : null;
        if (el) observer.observe(el);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [open, avoidSelector]);

    React.useLayoutEffect(() => {
        const host = anchorRef.current?.parentElement;
        if (!host) return undefined;
        const measure = () => {
            const rect = host.getBoundingClientRect();
            setCenterX(rect.left + rect.width / 2);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(host);
        window.addEventListener('resize', measure);
        return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
    }, []);

    React.useEffect(() => { if (!open) setMenuState(null); }, [open]);

    const muted = 'rgba(255, 255, 255, 0.72)';
    const actionSx = {
        height: 32,
        px: '11px',
        gap: '6px',
        borderRadius: '8px',
        color: muted,
        fontSize: '0.85rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)', color: '#fff' },
        '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.32)' },
        '& svg': { fontSize: 18, width: 18, height: 18 },
    };

    const activeAction = actions.find((a) => a.key === menuState?.key && a.menu);
    const closeMenu = () => setMenuState(null);

    return (
        <>
            <Box ref={anchorRef} sx={{ display: 'none' }} />
            <Slide direction="up" in={open && centerX != null} mountOnEnter unmountOnExit>
                <Box
                    role="toolbar"
                    aria-label="Bulk actions"
                    sx={{
                        position: 'fixed',
                        bottom,
                        // Slide animates `transform`, so centring uses a negative margin (set in the ref) instead of translateX.
                        left: centerX ?? '50%',
                        zIndex: 1300,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        height: 48,
                        pl: '8px',
                        pr: '8px',
                        // Size to every action so none is clipped; the max width still lets narrow screens scroll.
                        width: 'max-content',
                        maxWidth: 'calc(100vw - 32px)',
                        overflowX: 'auto',
                        borderRadius: '12px',
                        backgroundColor: theme.palette.customColors.g400,
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.18), 0 4px 6px -4px rgba(0,0,0,0.18)',
                    }}
                    ref={(node) => {
                        // Centre on the content: shift left by half the bar's own width.
                        if (node) node.style.marginLeft = `-${node.offsetWidth / 2}px`;
                    }}
                >
                    <ButtonBase
                        onClick={onClear}
                        aria-label="Clear selection"
                        sx={{
                            ...actionSx,
                            color: '#fff',
                            fontWeight: 600,
                            border: '1px solid rgba(255, 255, 255, 0.16)',
                            mr: '4px',
                        }}
                    >
                        <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                            {count} {noun}
                        </Typography>
                        <Close />
                    </ButtonBase>
                    <Box sx={{ width: '1px', height: 20, backgroundColor: theme.palette.customColors.g700, mx: '4px' }} />
                    {actions.map((action) => {
                        const isOpen = menuState?.key === action.key;
                        return (
                            <ButtonBase
                                key={action.key}
                                onClick={(e) => {
                                    if (!action.menu) { action.onClick?.(e); return; }
                                    const anchorEl = e.currentTarget;
                                    setMenuState((prev) => (prev?.key === action.key ? null : { key: action.key, anchorEl }));
                                }}
                                disabled={action.disabled || action.loading}
                                aria-haspopup={action.menu ? 'listbox' : undefined}
                                aria-expanded={action.menu ? isOpen : undefined}
                                sx={{
                                    ...actionSx,
                                    ...(isOpen && { backgroundColor: 'rgba(255, 255, 255, 0.12)', color: '#fff' }),
                                    ...(action.highlight && {
                                        color: '#fff',
                                        background: 'linear-gradient(135deg, rgba(120,82,248,0.35), rgba(132,174,246,0.25))',
                                        '&:hover': { background: 'linear-gradient(135deg, rgba(120,82,248,0.5), rgba(132,174,246,0.38))', color: '#fff' },
                                        // Shimmer: a soft light band sweeps across the button, paused for reduced-motion users.
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::after': {
                                            content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)',
                                            transform: 'translateX(-100%)',
                                            animation: 'aiShortlistShimmer 2.4s ease-in-out infinite',
                                            pointerEvents: 'none',
                                        },
                                        '@keyframes aiShortlistShimmer': {
                                            '0%': { transform: 'translateX(-100%)' },
                                            '60%, 100%': { transform: 'translateX(220%)' },
                                        },
                                        '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none', display: 'none' } },
                                    }),
                                }}
                            >
                                {action.loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : action.icon}
                                {action.label}
                                {action.menu && (
                                    <KeyboardArrowUp sx={{ ml: '-2px', transition: 'transform 0.15s ease', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                                )}
                            </ButtonBase>
                        );
                    })}
                </Box>
            </Slide>
            {activeAction && (
                <ActionPicker
                    anchorEl={menuState.anchorEl}
                    menu={activeAction.menu}
                    onClose={closeMenu}
                />
            )}
        </>
    );
};

// ClickUp-style picker that opens above a bar button: search, options, then footer links.
const ActionPicker = ({ anchorEl, menu, onClose }) => {
    const [query, setQuery] = React.useState('');
    const [highlighted, setHighlighted] = React.useState(0);
    const listRef = React.useRef(null);

    const options = (menu.options || []).filter((o) => o?.value !== 'All');
    const filtered = query.trim()
        ? options.filter((o) => String(o?.label ?? '').toLowerCase().includes(query.trim().toLowerCase()))
        : options;
    const footer = (menu.footer || []).filter(Boolean);

    React.useEffect(() => { setHighlighted(0); }, [query]);
    React.useEffect(() => {
        listRef.current?.querySelector(`[data-index="${highlighted}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [highlighted]);

    const choose = (option) => { onClose(); menu.onSelect?.(option); };
    const runFooter = (item) => { onClose(); item.onClick?.(); };

    const onKeyDown = (e) => {
        // Keep keys inside the picker (Esc would otherwise also clear the selection).
        e.stopPropagation();
        if (e.key === 'Escape') onClose();
        else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, filtered.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
        else if (e.key === 'Enter' && filtered[highlighted]) { e.preventDefault(); choose(filtered[highlighted]); }
    };

    const rowSx = {
        width: '100%',
        justifyContent: 'flex-start',
        gap: '10px',
        height: 36,
        px: '10px',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#344054',
        textAlign: 'left',
        '& svg': { fontSize: 18, width: 18, height: 18, color: '#667085', flexShrink: 0 },
    };

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement="top-start"
            modifiers={[{ name: 'offset', options: { offset: [0, 12] } }, { name: 'flip', enabled: false }]}
            sx={{ zIndex: 1301 }}
        >
            <ClickAwayListener
                onClickAway={(e) => { if (!anchorEl.contains(e.target)) onClose(); }}
            >
                <Box
                    onKeyDown={onKeyDown}
                    sx={{
                        width: 280,
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #E4E7EC',
                        boxShadow: '0 12px 24px -4px rgba(16,24,40,0.12), 0 4px 8px -2px rgba(16,24,40,0.06)',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: '10px', borderBottom: '1px solid #EAECF0' }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: '8px', height: 36, px: '10px',
                            border: '1px solid #D0D5DD', borderRadius: '8px',
                            '&:focus-within': { borderColor: '#7852F8', boxShadow: '0 0 0 3px rgba(120,82,248,0.12)' },
                        }}>
                            <Search sx={{ fontSize: 18, color: '#98A2B3' }} />
                            <InputBase
                                autoFocus
                                fullWidth
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={menu.searchPlaceholder || 'Search...'}
                                inputProps={{ 'aria-label': menu.searchPlaceholder || 'Search' }}
                                sx={{ fontSize: '0.875rem' }}
                            />
                        </Box>
                    </Box>
                    <Box ref={listRef} role="listbox" sx={{ maxHeight: 264, overflowY: 'auto', p: '6px' }}>
                        {menu.title && (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#667085', px: '10px', pt: '4px', pb: '6px' }}>
                                {menu.title}
                            </Typography>
                        )}
                        {filtered.length === 0 && (
                            <Typography sx={{ fontSize: '0.875rem', color: '#98A2B3', px: '10px', py: '10px' }}>No matches</Typography>
                        )}
                        {filtered.map((option, index) => (
                            <ButtonBase
                                key={option.value}
                                role="option"
                                data-index={index}
                                aria-selected={index === highlighted}
                                onMouseEnter={() => setHighlighted(index)}
                                onClick={() => choose(option)}
                                sx={{ ...rowSx, backgroundColor: index === highlighted ? '#F2F4F7' : 'transparent' }}
                            >
                                {menu.optionIcon}
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {capitalize(String(option.label ?? ''))}
                                </Box>
                            </ButtonBase>
                        ))}
                    </Box>
                    {footer.length > 0 && (
                        <Box sx={{ borderTop: '1px solid #EAECF0', p: '6px' }}>
                            {footer.map((item) => (
                                <ButtonBase
                                    key={item.key}
                                    onClick={() => runFooter(item)}
                                    sx={{ ...rowSx, color: '#475467', '&:hover': { backgroundColor: '#F2F4F7' } }}
                                >
                                    {item.icon}
                                    {item.label}
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                </Box>
            </ClickAwayListener>
        </Popper>
    );
};

export default SelectionActionBar;
